import {
  graphQuery,
  graphQueryBang,
  parseGraphValidationErrors,
} from "shared/lib/graphql"
import {
  AppletFilterCodeValidationMutation,
  AppletTriggerFieldValidationMutation,
  AppletQueryFieldValidationMutation,
  AppletActionFieldValidationMutation,
  AutoActivateMutation,
  DIYAppletEditMutation,
  DIYCreateAppletMutation,
  FilterCodeGeneratorsQuery,
  LoadPrivateServicesQuery,
  NormalizedAppletEnableMutation,
  StatementPreviewMutation,
} from "./queries"
import { gqlAliasStepModuleName } from "shared/lib/utils"

const brandOwnerDIY = ({ serviceSlug, trigger, actions }) => {
  if (serviceSlug === trigger.service.module_name) {
    return trigger.service.id
  } else {
    return actions[0].service.id
  }
}

const formatValue = (field_ui_type, value) => {
  if (value) {
    return value
  } else if (field_ui_type === "checkbox_multi") {
    return []
  } else {
    return ""
  }
}

const formatFields = step =>
  Object.values(step.fields)
    .sort((f1, f2) => f1.order - f2.order)
    .filter(f => f.name !== "_live_channel_id")
    .map(field => {
      const value = field.value
        ? JSON.stringify(formatValue(field.field_ui_type, field.value))
        : field.default_value_json
      return {
        name: field.name,
        hidden: field.hidden,
        value: value || JSON.stringify(""),
      }
    })

const formatStep = step => ({
  step_identifier: step.full_module_name,
  fields: formatFields(step),
  channel_id: step.service.id,
  live_channel_id: step.live_channel_id,
})

export const createApplet = ({
  actions,
  description,
  filterCode,
  pushEnabled,
  queries,
  serviceSlug,
  trigger,
}) =>
  graphQueryBang(DIYCreateAppletMutation, {
    name: description,
    push_enabled: pushEnabled,
    channel_id: brandOwnerDIY({
      trigger,
      actions,
      serviceSlug,
    }),
    filter_code: filterCode,
    trigger: formatStep(trigger),
    actions: actions.map(formatStep),
    queries: queries.map(formatStep),
  })

export const editApplet = ({
  appletId,
  name,
  trigger,
  queries,
  actions,
  filterCode,
}) =>
  graphQueryBang(DIYAppletEditMutation, {
    applet_id: appletId,
    channel_id: trigger.service.id,
    filter_code: filterCode,
    trigger: formatStep(trigger),
    actions: actions.map(formatStep),
    queries: queries.map(formatStep),
    name,
  })

const buildStoredField = (storedFields, fields, stepLiveChannelId) => {
  return fields.reduce((acc, cur) => {
    if (!cur.hidden) {
      return {
        ...acc,
        [cur.owner]: {
          ...acc[cur.owner],
          _live_channel_id: stepLiveChannelId,
          [cur.name]: cur.value,
        },
      }
    } else {
      return acc
    }
  }, storedFields)
}

export const updateCommunityApplet = ({
  appletId,
  name,
  pushEnabled,
  selectedTrigger,
  selectedActions,
  selectedQueries,
  metadata,
  filterCode,
}) => {
  let storedFields = {}
  storedFields = buildStoredField(
    storedFields,
    selectedTrigger.trigger_fields,
    selectedTrigger.live_channel_id
  )
  selectedQueries.forEach(selectedQuery => {
    storedFields = buildStoredField(
      storedFields,
      selectedQuery.query_fields,
      selectedQuery.live_channel_id
    )
  })
  selectedActions.forEach(selectedAction => {
    storedFields = buildStoredField(
      storedFields,
      selectedAction.action_fields,
      selectedAction.live_channel_id
    )
  })

  return graphQueryBang(NormalizedAppletEnableMutation, {
    applet_id: appletId,
    name,
    preserve_applet_fields: false,
    push_enabled: pushEnabled,
    dynamic_applet_configuration: false,
    stored_fields: storedFields,
    metadata,
    filter_code: filterCode,
  })
}

export const loadPreview = ({
  trigger,
  triggerFields,
  action,
  actionFields,
  actionLiveChannelId,
  triggerLiveChannelId,
}) => {
  const formatFieldsKeyValue = fields => {
    return Object.values(fields).reduce(
      (ac, field) => ({
        ...ac,
        [field.name]: field.value,
      }),
      {}
    )
  }

  const storedFields = {
    trigger,
    trigger_fields: formatFieldsKeyValue(triggerFields),
    action,
    action_fields: formatFieldsKeyValue(actionFields),
    trigger_live_channel_id: triggerLiveChannelId,
    action_live_channel_id: actionLiveChannelId,
  }

  return graphQueryBang(StatementPreviewMutation, {
    stored_fields: storedFields,
  })
}

// Make only one request to get all default values
export const loadActionDefaultValues = ({
  trigger_module_name,
  actionService,
}) => {
  let query = "query {"
  actionService.public_actions.map(action => {
    const stepModuleName = gqlAliasStepModuleName(action.module_name)
    query += `
      ${stepModuleName}: action(module_name: "${action.full_module_name}") {
        defaults_for_trigger(trigger_module_name: "${trigger_module_name}")
      }`
  })
  query += "}"

  return graphQueryBang(query)
}

export const loadPrivateServices = async () => {
  const {
    me: { private_channels },
  } = await graphQueryBang(LoadPrivateServicesQuery)

  return private_channels.map(service => {
    const image =
      service.lrg_monochrome_image_url || service.lrg_variant_image_url
    delete service.lrg_monochrome_image_url
    delete service.lrg_variant_image_url

    return {
      ...service,
      brand_color: `#${service.brand_color}`,
      image,
    }
  })
}

export const loadFilterCodeGenerators = async (
  triggerIds,
  queryIds,
  actionIds
) => {
  const result = await graphQuery(FilterCodeGeneratorsQuery, {
    triggerIds,
    queryIds,
    actionIds,
  })

  return result.data.filter_code_generators
}

export function validateFilterCode({ filter_code, trigger, queries, actions }) {
  return graphQueryBang(AppletFilterCodeValidationMutation, {
    filter_code,
    trigger: formatStep(trigger),
    actions: actions.map(formatStep),
    queries: queries.map(formatStep),
  }).then(response => response.appletFilterCodeValidation)
}

export function checkServiceActivation({ serviceModuleName }) {
  return graphQueryBang(AutoActivateMutation, {
    serviceModuleName,
    replace: false,
  })
}

export function validateStepField(stepType, payload) {
  const query = {
    trigger: AppletTriggerFieldValidationMutation,
    query: AppletQueryFieldValidationMutation,
    action: AppletActionFieldValidationMutation,
  }[stepType]

  return new Promise(resolve => {
    graphQuery(query, payload).then(response => {
      let errors = response?.data?.appletStepFieldValidation?.errors

      if (errors?.length) {
        errors = parseGraphValidationErrors(errors)
      }

      resolve(errors)
    })
  })
}
